<template>
  <div>
    <div class=""
         v-if="isGranted([permissions.PROJECT_VIEW, permissions.PROJECT_VIEW_OWN], project) && project.control_type === 'one time'"
         data-qid="ControlAssessmentScopeTable"
    >
      <header class="panel__header">
        <div class="profile-compact">
          <div class="logo-container">
            <font-awesome-icon icon="calendar-alt" class="icon-svg--sm"/>
          </div>
          <div class="profile-compact__info">
            <div class="profile-compact__top">
              <div class="profile-compact__text">
                {{ $t('projects.view.scope_period_title') }}
                <span class="section-compact__secondary"></span>
              </div>
            </div>
          </div>
        </div>

        <div class="profile-compact__actions">
          <span class="icon-btn"
                @click="showScopeFormFunction"
                data-qid="edit-button"
                v-if="(!showScopeForm && isGranted([permissions.PROJECT_EDIT, permissions.PROJECT_EDIT_OWN], project) && project.status !== 'closed' && project.status !== 'completed') && ((project.additional_client && getClient.slug !== project.additional_client.slug) || project.additional_client === null)"
          >
            <font-awesome-icon icon="pen"/>
          </span>
          <span class="icon-btn btn-delete"
                @click="cancelScopeForm"
                data-qid="cancel-button"
                v-if="showScopeForm"
          >
            <font-awesome-icon icon="rotate-left"/>
          </span>
          <span class="icon-btn btn-edit"
                @click="formScopeSubmit"
                data-qid="submit-button"
                v-if="showScopeForm"
          >
            <font-awesome-icon icon="save"/>
          </span>
        </div>
      </header>

      <div class="panel__body">
        <!-- One time -->
        <div class="row">
          <div class="col-sm-12">
            <div class="">
              <div class="mw-500" v-if="showScopeForm">
                <b-form-group v-if="project.type === 'control_assessment'" data-qid="scope-form-project-type">
                  <label>{{ $t('projects.create.project_type') }}</label>
                  <b-form-radio-group v-model="form.project_type" :options="projectTypeOptions"></b-form-radio-group>
                  <small class="form-text text-muted">{{ $t('projects.engagement.settings.audit_type_hint') }}</small>
                </b-form-group>
                <label>{{ formIsPointInTime ? $t('system.point_in_time') : $t('projects.scope.start_date_label') }}</label>
                <b-alert v-if="error.length"
                         show
                         variant="danger"
                         qid="create-s-error"
                >
                  {{ error }}
                </b-alert>
                <datepicker
                        class="date-picker"
                        qid="form-start-date"
                        calendar-class="left"
                        v-model="form.scope_start_date"
                        @selected="form.scope_end_date = ''"
                        :required="true"
                        :clear-button="true"
                        :placeholder="$t('projects.scope.start_date_placeholder')"
                ></datepicker>
              </div>
              <!-- Project type and scope period as cards, like the Schedule panel -->
              <div v-if="!showScopeForm" class="schedule-dates" data-qid="scope-period-view">
                <div v-if="project.project_type && project.type === 'control_assessment'" class="schedule-dates__item" data-qid="scope-project-type">
                  <div class="schedule-dates__label">
                    <font-awesome-icon icon="clipboard-list" class="mr-1"/>{{ $t('projects.create.project_type') }}
                  </div>
                  <div class="schedule-dates__date">
                    {{ project.project_type === 'type two' ? $t('system.type_two') : $t('system.type_one') }}
                  </div>
                  <div v-if="typeMismatch" class="schedule-dates__relative schedule-dates__relative--soon">
                    <font-awesome-icon icon="exclamation-triangle" class="mr-1"/>{{ $t('projects.scope.type_mismatch') }}
                  </div>
                </div>
                <div v-if="project.type === 'control_assessment'" class="scope-type-hint" data-qid="scope-project-type-hint">
                  <font-awesome-icon icon="info-circle" class="mr-1"/>{{ $t('projects.engagement.settings.audit_type_hint') }}
                </div>

                <div class="schedule-dates__item" data-qid="scope-period">
                  <div class="schedule-dates__label">
                    <font-awesome-icon icon="calendar-alt" class="mr-1"/>
                    <template v-if="isPointInTime">{{ $t('system.point_in_time') }}</template>
                    <template v-else>{{ $t('projects.view.scope_period_title') }}</template>
                  </div>
                  <template v-if="isPointInTime">
                    <div class="schedule-dates__date">{{ project.scope_start_date | formatDate('DD MMM YYYY', true) }}</div>
                  </template>
                  <template v-else-if="project.scope_start_date && project.scope_end_date">
                    <div class="schedule-dates__date">
                      {{ project.scope_start_date | formatDate('DD MMM YYYY', true) }} – {{ project.scope_end_date | formatDate('DD MMM YYYY', true) }}
                    </div>
                    <div class="schedule-dates__relative">{{ $tc('projects.scope.period_length', periodDays, { count: periodDays }) }}</div>
                  </template>
                  <div v-else class="schedule-dates__date text-muted">{{ $t('system.missing_input') }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- A point-in-time audit has a single date -->
        <div class="row" v-if="showScopeForm && !formIsPointInTime">
          <div class="col-sm-12">
            <div class="">
              <label class="mt-2">{{ $t('projects.scope.end_date_label') }}</label>
              <div class="mw-500">
                <datepicker
                        class="date-picker"
                        qid="form-end-date"
                        calendar-class="left"
                        v-model="form.scope_end_date"
                        :required="true"
                        :clear-button="true"
                        :placeholder="$t('projects.scope.end_date_placeholder')"
                ></datepicker>
              </div>
            </div>
          </div>
        </div>

        <div class="scope-description" data-qid="scope-description">
          <label class="mb-1">{{ $t('projects.scope.message_label') }}</label>
          <RichTextEditor v-if="showScopeForm"
                          qid="project-message-description"
                          v-model="form.scope_description"
                          :rows="6"
                          :headings="false"
                          compact
                          class="mw-500"
                          :placeholder="$t('projects.scope.message_placeholder')"
          />
          <RichTextView v-else-if="project.scope_description"
                        :value="project.scope_description"
                        :headings="false"
                        compact
                        class="scope-description__text text-formatted"
          />
          <p v-else class="scope-description__text text-muted">{{ $t('system.missing_input') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Datepicker from 'vuejs-datepicker';
  import RichTextEditor from '@/components/richText/RichTextEditor.vue';
  import RichTextView from '@/components/richText/RichTextView.vue';

  export default {
    name: 'ControlAssessmentScopeTable',
    data: function() {
      return {
        form: {
          control_scope_users: [],
          scope_start_date: '',
          scope_end_date: '',
          scope_description: '',
          project_type: 'type one'
        },
        projectTypeOptions: [
          {text: this.$t('system.type_one'), value: 'type one'},
          {text: this.$t('system.type_two'), value: 'type two'},
        ],
        error: '',
        showScopeForm: false,
        project: {},
        scopeOptions: [],
        radioOptions: [
          {text: this.$t('system.yes'), value: 'Yes'},
          {text: this.$t('system.no'), value: 'No'},
        ],
      }
    },
    props: {
      object: {
        type: Object
      },
      measureTask: {
        type: Object
      }
    },
    computed: {
      // Point-in-time audits (type one) only have a start date
      isPointInTime() {
        if (!this.project.scope_start_date) {
          return false;
        }
        return (this.project.type === 'control_assessment' && this.project.project_type === 'type one') || this.project.scope_start_date === this.project.scope_end_date;
      },
      formIsPointInTime() {
        return this.project.type === 'control_assessment' && this.form.project_type === 'type one';
      },
      // Length of the scope period in days, both ends included
      periodDays() {
        const moment = require('moment');
        const start = moment(String(this.project.scope_start_date).substring(0, 10), 'YYYY-MM-DD');
        const end = moment(String(this.project.scope_end_date).substring(0, 10), 'YYYY-MM-DD');
        return end.diff(start, 'days') + 1;
      },
      // A period audit (type two) needs a range, not a single date
      typeMismatch() {
        if (!this.project.scope_start_date || !this.project.scope_end_date) {
          return false;
        }
        return this.project.project_type === 'type two' && this.project.scope_start_date === this.project.scope_end_date;
      },
      getClient() {
        return this.$store.state.client
      }
    },
    components: {
      Datepicker,
      RichTextEditor,
      RichTextView
    },
    watch: {
      object: function (value) {
        this.project = value
      }
    },
    methods: {
      normalizeDate(value) {
        if (!value) {
          return null;
        }
        const datePart = String(value).substring(0, 10);
        const [year, month, day] = datePart.split('-').map(Number);
        return new Date(year, month - 1, day).getTime();
      },
      cancelScopeForm() {
        this.showScopeForm = false
      },
      formScopeSubmit() {
        var moment = require('moment');
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;

        self.form.scope_start_date = self.form.scope_start_date ? self.form.scope_start_date : ''
        // A point-in-time audit ends on the day it starts
        self.form.scope_end_date = this.formIsPointInTime ? self.form.scope_start_date : (self.form.scope_end_date ? self.form.scope_end_date : '')

        if (!this.form.scope_start_date || !this.form.scope_end_date) {
          if (!this.form.scope_start_date) {
            this.error = this.$t('projects.schedule.start_date_error')
          }
          if (!this.form.scope_end_date) {
            this.error = this.error + ' ' + this.$t('projects.schedule.end_date_error')
          }
        } else if (this.form.scope_start_date > this.form.scope_end_date) {
          this.error = this.$t('projects.schedule.start_date_greater_error')
        } else {
          piincHttp.put('projects/' + this.project.slug, this.form).then(function(response) {
            self.project = response.data
            self.$emit('updated');
            self.showScopeForm = false;
            self.setDefaultForm(self.project);
            self.$toastr('success', self.$t('projects.update.success'))
          }, function(error) {
            if (error.status !== 200) {
              self.error = error.data.message;
            }
          })
        }
      },
      showScopeFormFunction() {
        let self = this;
        this.setDefaultForm(this.project)
        this.showScopeForm = true
      },
      loadProcess() {
        let self = this;
        piincHttp.get('processes/' + this.project.slug, { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.project = response.data;
        }, function() {});
      },
      setDefaultForm(object) {
        var moment = require('moment');

        this.form.scope_start_date = object.scope_start_date ? object.scope_start_date : moment().toDate();
        if (object.scope_end_date) {
          this.form.scope_end_date = object.scope_end_date
        } else {
          this.form.scope_end_date = moment().add(14, 'day').toDate()
        }

        this.form.scope_description = object.scope_description;
        this.form.project_type = object.project_type || 'type one';

      },
    },
    created () {
      this.project = this.object
    }
  }
</script>
